import React from "react";

// The seam where a client-side error backend plugs in. New Relic was that
// backend until 2026-08-17, when we left the vendor; Rollbar was one before
// that. There is no vendor now: crashes POST to pop-core's /client_errors and
// land in the same `system_errors` table every server-side failure lands in,
// with the same admin triage index and the same `resolved` flag.
//
// `error` reports. `info` deliberately does not, and survives only so the 33
// forms calling `useErrorLogger.reportErrors` compile unchanged: that hook
// fires once per failed field, and burying an ops table in validation
// telemetry would hide the crashes it exists to surface.

export interface Logger {
  info: (message: string) => void;
  error: (message: string, error?: Error) => void;
}

interface Context {
  logger?: Logger;
}

export const ErrorLoggerContext = React.createContext<Context>({
  logger: undefined,
});

interface Props {
  children?: React.ReactNode;
}

// Same derivation as graphqlApiUrl in graphql/apolloClient, but its own
// variable: the reporter has to outlive the GraphQL host it sits beside, and
// old-core is a frozen line with a sunset date while /client_errors is not
// on it. Unset, it follows the API host, which is right for local dev.
const reportUrl = `${
  process.env.REACT_APP_ERROR_REPORT_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001"
}/client_errors`;

// The first frame that names a real file. `error.stack`'s opening line is the
// message, not a frame, and Safari omits the `at ` prefix entirely — so match
// on a URL with a line:column suffix rather than on frame syntax.
const FRAME = /((?:https?:)?\/\/[^\s()]+?|[\w./-]+\.[jt]sx?):(\d+):\d+/;

// A render loop that throws does so every frame. The server throttles by IP
// and the reports would be identical anyway, so stop after a handful — the
// first one is the one that gets triaged.
const MAX_REPORTS = 10;
let reportCount = 0;

const report = (name: string, message: string, stack?: string) => {
  if (reportCount >= MAX_REPORTS) return;
  reportCount += 1;

  const frame = stack?.match(FRAME);

  // Fire and forget. A failed report must never re-enter componentDidCatch,
  // and a 404 is the expected answer until pop-core ships the endpoint.
  fetch(reportUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      message,
      file: frame?.[1] ?? window.location.href,
      line: frame?.[2] ?? 0,
      stack: stack?.split("\n").slice(0, 50) ?? [],
    }),
    keepalive: true,
  }).catch(() => undefined);
};

// "Script error." is what the browser reports for a cross-origin script it
// refuses to describe — a browser extension, or a third-party tag. It carries
// no file, no line and no stack, so it is unactionable by construction.
const isNoise = (message: string) =>
  !message ||
  message === "Script error." ||
  message.startsWith("ResizeObserver");

export const ErrorLoggerProvider = ({ children }: Props): React.JSX.Element => {
  const logger: Logger = React.useMemo(
    () => ({
      info: () => undefined,
      error: (message, error) =>
        report(error?.name ?? "Error", message, error?.stack),
    }),
    []
  );

  // ErrorBoundary only sees errors thrown during render and lifecycle. An
  // event handler that throws, and every rejected promise, bypass it entirely
  // — New Relic used to catch those and nothing has since. Both land here.
  React.useEffect(() => {
    const onError = ({ message, error }: ErrorEvent) => {
      if (isNoise(message)) return;

      report(error?.name ?? "Error", message, error?.stack);
    };

    const onRejection = ({ reason }: PromiseRejectionEvent) => {
      const error = reason instanceof Error ? reason : undefined;
      const message = error?.message ?? String(reason);

      if (isNoise(message)) return;

      report(error?.name ?? "UnhandledRejection", message, error?.stack);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <ErrorLoggerContext.Provider value={{ logger }}>
      {children}
    </ErrorLoggerContext.Provider>
  );
};
